import {gql} from '@apollo/client';
import {useMutation$} from 'graphqlsupport';
import {Offer} from 'offer';
import {useCallback} from 'react';
import {useDomainErrorDialog} from '../../common/component/ErrorDialog';
import {sanitizeOfferInput, OfferFormInput} from '../component/OfferForm';
import {OfferContentFragment, StatusAndSubmitter} from '../domain/graphql';
import { ReferralSuggestionDecisionInput } from './useHigherPrioritySuggestion$';



interface PriorityReferralDecisionInput extends ReferralSuggestionDecisionInput {
    suggestedReferralGuid: string | null | undefined;
}

type NewOfferMutationInput = {
    referralGuid: string;
    offerInput: OfferFormInput;
    referralSuggestionDecision: PriorityReferralDecisionInput | null;
}
type NewOfferMutationResult = {
    submitNewOffer: Partial<Offer>;
};
const NEW_OFFER_MUTATION = gql`
mutation (
    $referralGuid: String!, 
    $offerInput: OfferContentInput,
    $referralSuggestionDecision: PriorityReferralDecisionInput
) {
  submitNewOffer(
    referralGuid: $referralGuid
    payload: $offerInput
    referralSuggestionDecision: $referralSuggestionDecision
  ) {
    id
    guid
    ...${OfferContentFragment.name}
    ...${StatusAndSubmitter.name}
  }
}
${OfferContentFragment.value}
${StatusAndSubmitter.value}
`;
export const useSubmitNewOffer$ = (
    referralGuid: string,
    onSuccess: (newOffer: Partial<Offer>) => any
) => {
    const {
        isRunning$,
        execute,
        data,
    } = useMutation$<NewOfferMutationInput, NewOfferMutationResult>(NEW_OFFER_MUTATION);

    const {errorMessage$, tryParsingDomainError} = useDomainErrorDialog();

    const submitOffer = useCallback(async (content: OfferFormInput, suggestionResult: PriorityReferralDecisionInput | null = null) => {
        try {
            const {data: result} = await execute({
                variables: {
                    referralGuid,
                    offerInput: sanitizeOfferInput(content),
                    referralSuggestionDecision: suggestionResult ?? null
                }
            });

            if (!!result) {
                onSuccess(result.submitNewOffer);
            }
        } catch (e) {
            if (!tryParsingDomainError(e)) {
                throw e;
            }
        }
    }, [execute, referralGuid, onSuccess, tryParsingDomainError]);

    return {
        isRunning$,
        submitOffer,
        data,
        errorMessage$,
    };
};
